import styled from '@emotion/styled';
import { captureException } from '@sentry/minimal';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Redirect, RouteComponentProps, useHistory } from 'react-router-dom';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useDispatch, useSelector } from 'react-redux';

import {
    BodyTextSmall,
    HeadingText20,
    HeadingText50,
    Button as ButtonBase,
    HeadingText40,
    pxToRem,
} from '@modernhealth/vital-components';

import { colors, layout, mediaQuery } from '@modernhealth/vital-foundation';

import { appClient } from 'src/config/apollo';
import { config } from 'src/config/config';
import { toastNotificationsService } from 'src/config/rootService';
import {
    ContentCollectionLinkLinkType,
    ScheduledContentActivityType,
    ScheduledContentFeedbackRatingType,
    ScheduledContentType,
} from 'src/generated/webapp_gql';
import { color } from 'src/shared/styles/variables';
import { Markdown } from 'src/modules/newPrograms/components/Markdown';
import { getUserInformation, getUserPreference } from 'src/modules/user/selectors/userSelectors';
import { SharedPreferencesKeys } from 'src/modules/user/models/user';
import { capture } from 'src/shared/helpers/sentryCapture';
import { Dispatch } from 'src/config/rootStore';

import {
    useGetScheduledContentQuery,
    GetScheduledContentDocument,
    GetScheduledContentQuery,
    GetScheduledContentQueryVariables,
} from '../documents/getScheduledContent.generated';
import { useRateScheduledContentMutation } from '../documents/rateScheduledContentMutation.generated';
import { useUpdateScheduledContentActivityMutation } from '../documents/updateScheduledContentActivity.generated';
import { useUpdateAccountSharedPreferencesMutation } from '../documents/updateAccountSharedPreferencesMutation.generated';
import { milestones } from '../util/milestones';
import { DailyPausePageSkeletons } from '../components/DailyPauseSkeletons';
import { DailyPauseFeedbackTestId, DailyPausePageTestId } from './testIds';

export const dailyPauseBannerHeight = pxToRem(180);

const Container = styled.div({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    flexGrow: 1,
    backgroundColor: color.white,
    overflow: 'hidden',
    margin: '0 auto',

    width: '100%',
    maxWidth: layout.containerWidth,
    paddingLeft: layout.containerPaddingX,
    paddingRight: layout.containerPaddingX,
    paddingTop: pxToRem(16),
    paddingBottom: pxToRem(24),
});

export const ContentMaxWidthWrapper = styled.div({
    width: '100%',

    [mediaQuery.minWidth('sm')]: {
        maxWidth: '80%',
    },

    [mediaQuery.minWidth('md')]: {
        maxWidth: '50%',
    },
});

const LinkContainer = styled.div({
    marginTop: 36,
    marginBottom: 24,
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    [mediaQuery.minWidth('sm')]: {
        flexDirection: 'row',
    },
    gap: pxToRem(16),
});

const Banner = styled.div<{ accentColor: string }>(({ accentColor }) => ({
    height: dailyPauseBannerHeight,
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
    marginBottom: pxToRem(14),
    borderRadius: 8,
    backgroundColor: accentColor,
    overflow: 'hidden',
}));

const ContentWrapper = styled.div({
    paddingTop: 16,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
});

const TextContentWrapper = styled.div({
    marginTop: 12,
    marginBottom: 48,
});

const MarkdownContainer = styled.div({
    '.markdown p': {
        marginBottom: 10,
    },
});

const FeedbackSection = styled.div({
    marginBottom: pxToRem(48),
});

const RatingContainer = styled.div({
    display: 'flex',
    justifyContent: 'center',
});

const EmojiContainer = styled.span<{
    isSelected: boolean;
    isFaded: boolean;
}>(({ isSelected, isFaded }) => ({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: pxToRem(54),
    height: pxToRem(54),
    backgroundColor: colors.gray05,
    borderRadius: '50%',
    cursor: 'pointer',
    opacity: isFaded ? 1 : 0.5,
    border: isSelected ? `1px solid ${colors.gray30}` : '1px solid transparent',
}));

const Button = styled(ButtonBase)({
    width: '100%',
});

interface MatchParams {
    uuid?: string;
}

type Props = RouteComponentProps<MatchParams, {}, { uuid?: string; from?: boolean } | undefined>;

const ratingEmojis = [
    { icon: '👎', rating: ScheduledContentFeedbackRatingType.Negative },
    {
        icon: '😐',
        rating: ScheduledContentFeedbackRatingType.Neutral,
        style: { marginLeft: pxToRem(48), marginRight: pxToRem(48) },
    },
    { icon: '👍', rating: ScheduledContentFeedbackRatingType.Positive },
];

export const DailyPause: React.FC<Props> = ({ match, location }) => {
    const { t } = useTranslation();
    const history = useHistory();
    const {
        releaseDiginShowDailyPauseStreaksWeb,
        dailyPauseStreaksExperimentVariant,
        startDailyPauseStreaksExperimentWeb,
    } = useFlags();
    const showStreaksInfo =
        startDailyPauseStreaksExperimentWeb &&
        dailyPauseStreaksExperimentVariant === 'treatment' &&
        releaseDiginShowDailyPauseStreaksWeb;
    const user = useSelector(getUserInformation);
    const canBeBucketedForDPStreaks = useSelector(
        getUserPreference(SharedPreferencesKeys.CanBeBucketedForDPStreaks),
    );

    const dispatch = useDispatch<Dispatch>();
    const [isInitiallyCompleted, setInitiallyCompleted] = useState(true);

    // Get the Pause uuid from the route parameter
    let { uuid = null } = match.params;
    // Fallback to internal state otherwise
    if (!uuid && location.state?.uuid) {
        uuid = location.state.uuid;
    }

    const getScheduledContentVariables = { uuid, contentType: ScheduledContentType.DailyPause };
    const {
        data,
        loading: getScheduledContentLoading,
        error: getScheduledContentError,
        refetch,
    } = useGetScheduledContentQuery({
        variables: getScheduledContentVariables,
        fetchPolicy: 'cache-and-network',
        // When we call refetch() we don't want this component to
        // update.
        notifyOnNetworkStatusChange: false,
        client: appClient,
    });
    const scheduledContent = data?.scheduledContent;
    const scheduledContentUuid = scheduledContent?.uuid;
    const refetchRef = useRef(refetch);
    refetchRef.current = refetch;
    const [didJustVote, setDidJustVote] = useState(false);

    const [rateScheduledContent, { loading: ratingDataLoading }] = useRateScheduledContentMutation({
        client: appClient,
        update: (cache, { data }) => {
            const scheduledContent = cache.readQuery<
                GetScheduledContentQuery,
                GetScheduledContentQueryVariables
            >({
                query: GetScheduledContentDocument,
                variables: getScheduledContentVariables,
            });

            if (!scheduledContent?.scheduledContent) {
                return;
            }

            const updatedScheduledContent: GetScheduledContentQuery = {
                ...scheduledContent,
                scheduledContent: {
                    ...scheduledContent.scheduledContent,
                    feedback: data?.rateScheduledContent?.scheduledContentFeedback ?? null,
                },
            };

            cache.writeQuery<GetScheduledContentQuery, GetScheduledContentQueryVariables>({
                query: GetScheduledContentDocument,
                variables: getScheduledContentVariables,
                data: updatedScheduledContent,
            });
        },
    });

    const [updateActivity, { loading: updateActivityLoading, error: updateActivityError }] =
        useUpdateScheduledContentActivityMutation({
            client: appClient,
            fetchPolicy: 'no-cache',
        });

    const [updateAccountSharedPreferencesMutation] = useUpdateAccountSharedPreferencesMutation({
        client: appClient,
    });

    const handleRateScheduledContent = async (rating: ScheduledContentFeedbackRatingType) => {
        try {
            await rateScheduledContent({
                variables: {
                    rating,
                    scheduledContentUuid,
                },
            });
            setDidJustVote(true);
        } catch (err) {
            captureException(err);
        }
    };

    const cta = useMemo(() => {
        return scheduledContent?.scheduledContentLinks?.find(link => link.isCta) ?? null;
    }, [scheduledContent]);

    const buildCtaButton = () => {
        if (!cta) {
            return null;
        }

        const {
            linkType,
            mobileValue: mobileLink,
            webValue: webLink,
            title = t('dailyPause:content_modal.cta_fallback'),
        } = cta;

        switch (linkType) {
            case ContentCollectionLinkLinkType.Meditation:
                return (
                    <Button
                        size="large"
                        onClick={() => {
                            history.push(`/meditations?uuid=${webLink || mobileLink}`);
                        }}
                    >
                        {title}
                    </Button>
                );
            case ContentCollectionLinkLinkType.Program:
                return (
                    <Button
                        size="large"
                        onClick={() => {
                            history.push(`/programs?uuid=${webLink || mobileLink}`);
                        }}
                    >
                        {title}
                    </Button>
                );
            case ContentCollectionLinkLinkType.InternalLink:
                // Don't fallback to mobile links for internal links because
                // the member web app can't handle "deep links" like
                // modernhealth://circles
                if (!webLink) {
                    captureException(
                        new Error(
                            `Daily pause attempted to render an internal link (${
                                mobileLink || 'unknown'
                            }) on web but no corresponding web link was found.`,
                        ),
                    );
                    return null;
                }

                return (
                    <Button
                        size="large"
                        onClick={() => {
                            history.push(webLink);
                        }}
                    >
                        {title}
                    </Button>
                );
            case ContentCollectionLinkLinkType.ExternalLink:
                return (
                    <Button
                        size="large"
                        onClick={() => {
                            window.open(webLink || mobileLink, '_blank', 'noopener,noreferrer');
                        }}
                    >
                        {title}
                    </Button>
                );
            case ContentCollectionLinkLinkType.CircleTopic:
            case ContentCollectionLinkLinkType.CircleSeries:
            case ContentCollectionLinkLinkType.CircleSession:
                captureException(new Error(`Unhandled daily pause link type ${linkType}`));
                break;
            default:
                const exhaustiveCheck: never = linkType;

                captureException(
                    new Error(`Unrecognized daily pause link type ${exhaustiveCheck}`),
                );
        }

        return null;
    };

    useEffect(() => {
        if (getScheduledContentError) {
            toastNotificationsService.renderErrorToast(t(config.messages.genericError));
        }
    }, [getScheduledContentError, t]);

    useEffect(() => {
        if (
            !startDailyPauseStreaksExperimentWeb ||
            getScheduledContentError ||
            canBeBucketedForDPStreaks === 'true' // user already is bucketed
        ) {
            return;
        }

        const updatedUserPreferences = {
            key: SharedPreferencesKeys.CanBeBucketedForDPStreaks,
            value: 'true',
        };

        updateAccountSharedPreferencesMutation({
            variables: updatedUserPreferences,
        })
            .then(() => {
                dispatch.user.updateSharedPreferences(updatedUserPreferences);
            })
            .catch(capture);
    }, [
        startDailyPauseStreaksExperimentWeb,
        getScheduledContentError,
        updateAccountSharedPreferencesMutation,
        dispatch.user,
        canBeBucketedForDPStreaks,
    ]);

    useEffect(() => {
        if (scheduledContent) {
            setInitiallyCompleted(completed => completed && Boolean(scheduledContent?.isCompleted));
        }

        if (
            !scheduledContent?.isCompleted &&
            !getScheduledContentError &&
            !getScheduledContentLoading &&
            scheduledContentUuid
        ) {
            updateActivity({
                variables: {
                    uuid: scheduledContentUuid,
                    activityType: ScheduledContentActivityType.Completed,
                },
            })
                .catch(err => captureException(err))
                .finally(() => {
                    // Update the scheduled content.
                    // Because we're using a cache policy this will also trigger
                    // an update on the homescreen.
                    try {
                        refetchRef.current?.();
                    } catch {}
                });
        }
    }, [
        scheduledContentUuid,
        getScheduledContentLoading,
        getScheduledContentError,
        updateActivity,
        scheduledContent,
    ]);

    const accentBackground = scheduledContent?.accentColor
        ? `#${scheduledContent?.accentColor}`
        : color.white;

    const selectedRating = scheduledContent?.feedback?.rating;

    const dailyPauseRemindersPageVisited =
        user.sharedPreferences.find(
            item => item.key === SharedPreferencesKeys.DailyPauseRemindersPageVisited,
        )?.value === 'true';

    const handleDailyPauseFinish = () => {
        if (updateActivityError || getScheduledContentError) {
            toastNotificationsService.renderErrorToast(t(config.messages.genericError));
            history.push('/dashboard');
            return;
        }

        if (
            !getScheduledContentLoading &&
            !updateActivityLoading &&
            data?.scheduledContentActivity?.completedCount
        ) {
            const hasAchievedMilestone = milestones.includes(
                data.scheduledContentActivity.completedCount,
            );

            if (location?.state?.from) {
                return history.goBack();
            }

            if (showStreaksInfo) {
                if (isInitiallyCompleted) {
                    return history.push('/pause/stats');
                }

                return history.push({
                    pathname: '/pause/complete',
                    state: {
                        uuid: scheduledContentUuid,
                        dailyPauseRemindersPageVisited,
                        hasAchievedMilestone,
                    },
                });
            }

            return history.push({
                pathname: '/pause/complete',
                state: {
                    uuid: scheduledContentUuid,
                    dailyPauseRemindersPageVisited,
                },
            });
        }
    };

    const nextStepBlocked = getScheduledContentLoading || updateActivityLoading;

    if (!scheduledContent) {
        return getScheduledContentLoading ? (
            <DailyPausePageSkeletons testId={DailyPausePageTestId.SkeletonLoader} />
        ) : (
            <Redirect to="/dashboard" />
        );
    }

    return (
        <Container data-testid={DailyPausePageTestId.Container}>
            <Banner accentColor={accentBackground}>
                {scheduledContent.headerImage && <img src={scheduledContent.headerImage} alt="" />}
            </Banner>
            <ContentMaxWidthWrapper>
                <ContentWrapper>
                    <HeadingText40 fontColor="gray85">{scheduledContent.bannerTitle}</HeadingText40>

                    <TextContentWrapper>
                        <MarkdownContainer>
                            <Markdown text={scheduledContent.fullContent} />
                        </MarkdownContainer>
                    </TextContentWrapper>
                </ContentWrapper>
                {(!scheduledContent.feedback || didJustVote) && (
                    <FeedbackSection data-testid={DailyPauseFeedbackTestId.Container}>
                        <HeadingText20 centered marginBottom={16}>
                            {t('dailyPause:content_modal.rating_question')}
                        </HeadingText20>
                        <BodyTextSmall centered marginBottom={32}>
                            {t('dailyPause:content_modal.rating_info')}
                        </BodyTextSmall>
                        <RatingContainer>
                            {ratingEmojis.map(item => (
                                <EmojiContainer
                                    key={item.rating}
                                    onClick={() => handleRateScheduledContent(item.rating)}
                                    isFaded={
                                        selectedRating
                                            ? selectedRating === item.rating
                                            : !ratingDataLoading
                                    }
                                    isSelected={selectedRating === item.rating}
                                    style={item.style}
                                    data-testid={`feedback${item.rating}`}
                                >
                                    <HeadingText50>{item.icon}</HeadingText50>
                                </EmojiContainer>
                            ))}
                        </RatingContainer>
                    </FeedbackSection>
                )}
                <LinkContainer>
                    {cta ? <div style={{ flex: '3 0' }}>{buildCtaButton()}</div> : null}

                    <div style={{ flex: '2 1' }}>
                        <Button
                            variant="secondary"
                            size="large"
                            onClick={handleDailyPauseFinish}
                            data-testid={DailyPausePageTestId.DoneButton}
                            busy={nextStepBlocked}
                        >
                            {t('dailyPause:content_modal.done')}
                        </Button>
                    </div>
                </LinkContainer>
            </ContentMaxWidthWrapper>
        </Container>
    );
};
